import { assertIsNotUndefined } from "common/utils/assertOfType";
import type { Material } from "three";
import type { ExtendedMaterial } from "../threejs/three-extensions";
import type { SdfParams } from "./designMaterialTypes";

/**
 * @param material A design material returned by designMaterialNew.
 * @returns Design material with thumbnail mode specific settings applied.
 */
export function getDesignMaterialInThumbnailMode(material: Material): Material {
  assertIsNotUndefined(material.userData.materialParams);
  if (material.userData.isBaked) {
    return material;
  }

  // userData can contain references which clone() can't handle, so we reset userData before cloning.
  const oldUserData = material.userData;
  material.userData = {};
  const materialCopy = material.clone();
  material.userData = oldUserData;

  // Shallow copy should be enough for these values that are not handled by clone().
  materialCopy.userData = oldUserData;
  materialCopy.defines = material.defines;
  materialCopy.onBeforeCompile = material.onBeforeCompile;
  materialCopy.customProgramCacheKey = material.customProgramCacheKey;

  const prevProgramChange = (material as unknown as ExtendedMaterial).onProgramChange;
  const sdf: SdfParams = materialCopy.userData.materialParams.sdf;
  (materialCopy as unknown as ExtendedMaterial).onProgramChange = function (params: {
    uniforms: { [name: string]: any };
  }) {
    prevProgramChange(params);
    if (params.uniforms.parallaxAoStrength !== undefined) {
      params.uniforms.parallaxAoStrength.value = sdf.common.useAO
        ? Math.min(sdf.common.thumbnailAoStrength * 0.5, 1.0)
        : 0.0;
    } else if (params.uniforms.sdfAoStrength !== undefined) {
      params.uniforms.sdfAoStrength.value = sdf.common.useAO ? sdf.common.thumbnailAoStrength : 0.0;
      params.uniforms.sdfAoTolerance.value = sdf.common.thumbnailAoTolerance;
    }
  };
  return materialCopy;
}
